import { useState, useEffect } from "react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";

export interface SampleTableFieldFormData {
  sheetName: string;
  startRow: number;
  attributeHeaderRow: number;
  sampleNumberColumn: string;
  outcomeStartColumn: string;
}

interface SampleTableFieldFormProps {
  value: SampleTableFieldFormData;
  onChange: (value: SampleTableFieldFormData) => void;
  readOnly?: boolean;
}

export function SampleTableFieldForm({
  value,
  onChange,
  readOnly = false,
}: SampleTableFieldFormProps) {
  const [formData, setFormData] = useState<SampleTableFieldFormData>(value);

  useEffect(() => {
    setFormData(value);
  }, [value]);

  const handleChange = (
    field: keyof SampleTableFieldFormData,
    newValue: string | number,
  ) => {
    const updated = { ...formData, [field]: newValue };
    setFormData(updated);
    onChange(updated);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="sheetName">Sheet Name</Label>
        <Input
          id="sheetName"
          value={formData.sheetName}
          onChange={(e) => handleChange("sheetName", e.target.value)}
          placeholder="Sheet1"
          disabled={readOnly}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="attributeHeaderRow">Attribute Header Row</Label>
          <Input
            id="attributeHeaderRow"
            type="number"
            min={1}
            value={formData.attributeHeaderRow}
            onChange={(e) =>
              handleChange(
                "attributeHeaderRow",
                parseInt(e.target.value, 10) || 1,
              )
            }
            placeholder="1"
            disabled={readOnly}
          />
          <p className="text-sm text-muted-foreground">
            Row containing attribute names as column headers
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="startRow">Data Starting Row</Label>
          <Input
            id="startRow"
            type="number"
            min={1}
            value={formData.startRow}
            onChange={(e) =>
              handleChange("startRow", parseInt(e.target.value, 10) || 1)
            }
            placeholder="2"
            disabled={readOnly}
          />
          <p className="text-sm text-muted-foreground">
            First row of sample data (typically header row + 1)
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="sampleNumberColumn">Sample Number Column</Label>
        <Input
          id="sampleNumberColumn"
          value={formData.sampleNumberColumn}
          onChange={(e) =>
            handleChange("sampleNumberColumn", e.target.value.toUpperCase())
          }
          placeholder="A"
          disabled={readOnly}
          className="uppercase"
        />
        <p className="text-sm text-muted-foreground">
          Column for sample identifiers (e.g., Sample 1, Sample 2)
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="outcomeStartColumn">First Outcome Column</Label>
        <Input
          id="outcomeStartColumn"
          value={formData.outcomeStartColumn}
          onChange={(e) =>
            handleChange("outcomeStartColumn", e.target.value.toUpperCase())
          }
          placeholder="B"
          disabled={readOnly}
          className="uppercase"
        />
        <p className="text-sm text-muted-foreground">
          Starting column for attribute outcomes (expands rightward, one column
          per attribute)
        </p>
      </div>

      <div className="rounded-md bg-purple-50 border border-purple-200 p-3 text-sm">
        <strong>Preview:</strong> {formData.sheetName} - Matrix table
        <div className="mt-2 text-xs">
          Header Row: {formData.attributeHeaderRow} | Data Start: Row{" "}
          {formData.startRow}
        </div>
        <div className="mt-1 text-xs">
          Sample IDs in column {formData.sampleNumberColumn}, outcomes from
          column {formData.outcomeStartColumn} →
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          Expands: Vertically (one row per sample) and Horizontally (one column
          per attribute)
        </div>
      </div>
    </div>
  );
}
